'use client';

/**
 * ConversationHistoryDrawer — full-screen mobile history sheet (v3 T6).
 *
 * Refitted from the original 85vw side-sheet into a full-width sheet: below
 * `lg:` this is the ONLY way to browse history (the desktop HistorySidebar
 * takes over at `lg:` — see history-sidebar.tsx + conversation-header.tsx).
 * Reuses HistoryList (shared with HistorySidebar) so the two stay in sync,
 * plus the same DeviceBanner/IdentityFooter chrome as the sidebar.
 */

import { memo, useCallback, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { V3_COLORS } from '@/lib/design-tokens';
import { useConversationStore } from '@/stores/conversation-store';
import { useChatContext } from '@/contexts/ChatContext';
import {
  useConversationList,
  useDeleteConversation,
} from '@/hooks/useConversations';
import {
  injectActiveConversation,
  applyPreviewCache,
} from '@/components/assistant/conversation-history/utils';
import { useConversationPreviews } from '@/components/assistant/conversation-history/use-conversation-previews';
import { HistoryList } from '@/components/assistant/conversation-history/history-list';
import { DeviceBanner } from './device-banner';
import { IdentityFooter } from './identity-footer';
import { ExpandingCapsule, CAPSULE_SIZE } from './primitives/expanding-capsule';

// ── Types ─────────────────────────────────────────────────────────

interface ConversationHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ── Component ─────────────────────────────────────────────────────

export const ConversationHistoryDrawer = memo(
  function ConversationHistoryDrawer({
    open,
    onOpenChange,
  }: ConversationHistoryDrawerProps) {
    // ── Data fetching (only when the sheet is open) ──
    // isPending = first fetch, no cached data yet → HistoryList skeleton.
    const { data, isPending, isError, refetch } = useConversationList(
      50,
      0,
      open
    );
    const { mutate: deleteConversation } = useDeleteConversation();

    // ── Store selectors ──
    const conversationId = useConversationStore((s) => s.conversationId);
    const activeConversationTitle = useConversationStore(
      (s) => s.activeConversationTitle
    );
    const resumeConversation = useConversationStore(
      (s) => s.resumeConversation
    );
    // Via ChatContext (not the store directly): startNewChat is the single
    // "new chat" reset point — it also restores the landing headline.
    const { startNewChat, messages } = useChatContext();

    // ── Search state ──
    const [searchQuery, setSearchQuery] = useState('');

    // Session preview cache — same wiring as HistorySidebar (see
    // use-conversation-previews.ts).
    const previews = useConversationPreviews(messages);

    // Inject the active conversation so an in-progress chat (no backend row
    // yet) shows immediately (#83); deduped by id against the server list.
    const conversations = useMemo(
      () =>
        injectActiveConversation(
          applyPreviewCache(data?.conversations ?? [], previews),
          conversationId,
          activeConversationTitle,
          new Date().toISOString(),
          previews[conversationId] ?? null
        ),
      [data, conversationId, activeConversationTitle, previews]
    );

    // ── Handlers ──
    const handleSelect = useCallback(
      (id: string, title: string | null) => {
        // Tapping the already-open conversation just closes the sheet —
        // never tear down and reload the active session.
        if (id !== conversationId) {
          resumeConversation(id, title ?? undefined);
        }
        onOpenChange(false);
      },
      [conversationId, resumeConversation, onOpenChange]
    );

    const handleNewChat = useCallback(() => {
      startNewChat();
      onOpenChange(false);
    }, [startNewChat, onOpenChange]);

    const handleDelete = useCallback(
      (id: string, isLocalOnly: boolean) => {
        if (isLocalOnly) {
          // The injected active-conversation row — no server record exists
          // yet (see injectActiveConversation), so there's nothing to
          // DELETE. Local reset only, same as "New chat"; sheet stays open
          // like the branch below.
          startNewChat();
          return;
        }
        deleteConversation(id);
        // Same as HistorySidebar: deleting the OPEN conversation starts a
        // fresh chat so its synthetic row doesn't linger. The sheet stays
        // open — the user may want to pick another conversation next.
        if (id === conversationId) startNewChat();
      },
      [deleteConversation, conversationId, startNewChat]
    );

    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="left"
          showCloseButton={false}
          className={cn(
            'flex w-screen max-w-none flex-col gap-0 border-none p-0',
            'data-[side=left]:w-screen data-[side=left]:max-w-none data-[side=left]:sm:max-w-none',
            // v3-native slide (FB27 #2) — sheet.tsx's own animate classes
            // are dead Tailwind-v4 syntax in this repo; see the drawer-in
            // keyframe's comment in tailwind.config.ts. Radix waits for
            // animationend before unmounting, so drawer-out plays fully.
            'motion-safe:data-[state=open]:animate-drawer-in motion-safe:data-[state=closed]:animate-drawer-out'
          )}
          style={{ backgroundColor: V3_COLORS.surfaceSidebar }}
        >
          {/* ── Header ── */}
          <div className="flex shrink-0 items-center justify-between px-[16px] pb-[12px] pt-[20px]">
            <SheetTitle
              className="font-[family-name:var(--font-lora)] text-[19px] font-semibold"
              style={{ color: V3_COLORS.ink }}
            >
              Conversations
            </SheetTitle>
            <button
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              // Color transition merged with the shared press feel's
              // transform (see PRESSABLE's doc comment in design-tokens.ts).
              className="flex size-9 items-center justify-center rounded-full transition-[background-color,transform] duration-[160ms] ease-out hover:bg-black/5 motion-safe:active:scale-[0.96]"
              style={{ color: '#9a8f7f' }}
            >
              <X className="size-[18px]" />
            </button>
          </div>

          {/* ── Search + New ── */}
          <div className="flex shrink-0 items-center gap-2.5 px-[16px] pb-[12px]">
            <div className="relative min-w-0 flex-1">
              <Search
                aria-hidden="true"
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: '#b0a48f' }}
              />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations"
                className="h-9 w-full rounded-[20px] border-[1px] bg-white pl-9 pr-[12px] text-[13.5px] outline-none placeholder:text-[#b3a996]"
                style={{ borderColor: V3_COLORS.warmBorderAlt }}
              />
            </div>
            <ExpandingCapsule
              size={CAPSULE_SIZE.SM}
              label="New"
              onClick={handleNewChat}
            />
          </div>

          {/* Only when the list is non-empty — same convention as
              HistorySidebar (a device-identity notice above an empty list
              reads oddly). */}
          {conversations.length > 0 && <DeviceBanner />}

          {/* ── Conversation list (shared with HistorySidebar) ── */}
          <HistoryList
            conversations={conversations}
            activeConversationId={conversationId}
            searchQuery={searchQuery}
            onSelect={handleSelect}
            onDelete={handleDelete}
            isPending={isPending}
            isError={isError}
            onRetry={refetch}
            className="flex-1 px-2 pb-2"
          />

          <IdentityFooter />
        </SheetContent>
      </Sheet>
    );
  }
);
